import * as fs from 'fs-extra'
import * as yaml from 'js-yaml'
import * as path from 'path'
import type { IApplicationModel, KV } from "yam-types"
import { BUILT_IN_PARAMS, BUILT_IN_PARAM_MAP, DEFAULT_ENV_KEY, VALUES_DIR } from "../constants"
import { EnvPatternValues } from '../main'
import { ClientConfig, KubernetesEnvironment } from "../types.d"
import { createLogger, generateWorkloadNameWithEnv, isMultiEnvClusterMode, parseBaseType, randomAlphabetNumeric, reduceWildcardEnvValue } from '../util'
import execExpr from './expr'

/**
 * Match include expression like include('some/file.yaml')
 */
const includeTemplateMatcher = /^\s{0,3}include\(['"]?([^'")]{1,256}\.(yml|yaml))['"]?\)\s{0,3}$/g

/**
 * TemplateRender could resolving dynamic parameters from values/ dir,
 * another functionality is using dynamic values to rendering other templates.
 */
export default class TemplateRender {

  fileContentCache = new Map<string, string>()
  includeCache = new Set<string>()

  preInjectedEnvVars = new Map<string, Map<string, string>>()

  private log = createLogger('template')

  /**
   * Read parameter values from './values' folder and additional values sources
   */
  public async readCustomizedValues(
    workingDir: string,
    cmdParams: Map<string, string>,
    clusters: KubernetesEnvironment[],
    model: IApplicationModel,
    envLevelDefinitions?: string[],
    valueSources?: string[],
    clientConf?: ClientConfig,
    // from envDefinition
    additionalGlobalValues?: EnvPatternValues<{ [k: string]: KV }>[]
  ): Promise<{ values: Map<string, KV>, varDefinitions: VariableDefinition[], rawParameters: Map<string, KV> }> {
    // final result after transform, like { "someEnv": { "someParam" : "someValue" } }
    const result = new Map<string, KV>()

    const varDefinitions: VariableDefinition[] = []
    const varDefAdded = new Set()

    clusters.forEach(cluster => {
      if (clientConf) {
        // merge the additional global values of envDefinition
        const additionalValues = reduceWildcardEnvValue(cluster.name, additionalGlobalValues ?? []) ?? {}
        const gv = clientConf.globalValues ?? {}
        Object.keys(additionalValues).forEach(k => { gv[k] = { ...gv[k], ...additionalValues[k] } })
        const globalValues = composeValuesByEnv(cluster.envLocators, gv, 'global.')
        Object.keys(globalValues).forEach(k => {
          if (!varDefAdded.has(k)) {
            varDefinitions.push({
              name: k,
              builtIn: true,
              type: Array.isArray(globalValues[k]) ? 'array' : typeof globalValues[k]
            })
            varDefAdded.add(k)
          }
        })
        result.set(cluster.name, globalValues)
      }
    })

    // parameter first-class map for values: raw result read from values yaml
    // eg. { "someParam": { "someEnv": "someValue" } }, also env-tree based parameters are supported
    const rawParameters = new Map<string, KV>()
    const tmp = await this.readCustomizedValuesRecursive(rawParameters, workingDir, VALUES_DIR, 0)
    varDefinitions.push(...tmp)

    // merge additional parameters from dir/files
    if (valueSources && valueSources.length > 0) {
      for (const source of valueSources) {
        if (source && source.trim()) {
          this.log.info(`merge additional customized values: ${source}`)
          const tmp = await this.readCustomizedValuesRecursive(rawParameters, workingDir, source, 0, true)
          varDefinitions.push(...tmp)
        }
      }
    }

    clusters.forEach(cluster => {
      let values
      const parameters = composeValuesByEnv(cluster.envLocators, rawParameters)
      if (result.has(cluster.name)) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        values = { ...result.get!(cluster.name), ...parameters }
      } else {
        values = parameters
      }

      Object.keys(parameters).forEach(k => {
        this.log.info(`value of parameter '${k}' for env '${cluster.name}' fetched.`)
      })

      result.set(cluster.name, values)
    })

    const immutableVars = [
      BUILT_IN_PARAM_MAP.STACK, BUILT_IN_PARAM_MAP.ENVIRONMENT,
      ...(envLevelDefinitions || [BUILT_IN_PARAM_MAP.REGION, BUILT_IN_PARAM_MAP.ZONE])
    ]
    // append cmdParams into each cluster values map (including built-in params)
    result.forEach((paramKV) => cmdParams.forEach((v, k) => {
      // output info & warnings if the parameter is overridden by command line, except --version
      if (paramKV[k] !== undefined && k !== "version") {
        if (immutableVars.indexOf(k) !== -1) {
          this.log.warn(`built-in parameter ${k} is not allowed to be overridden, built-in params are: ${BUILT_IN_PARAMS.join(',')}.`)
          return
        } else {
          this.log.info(`parameter ${k} will be overridden by command line arg value: ${v}`)
        }
      }
      if (k === "version") {
        // no parse for version param, in case of none semantic versioning like 1.0/2.1
        paramKV[k] = v
      } else {
        paramKV[k] = this.parseBaseType(v)
      }
      // add var definition
      varDefinitions.push({
        name: k,
        builtIn: true,
        type: paramKV[k] instanceof Array ? 'array' : (typeof paramKV[k])
      })
    }))

    // built-in parameter injection
    clusters.forEach(cluster => {
      const paramKV = result.get(cluster.name)
      if (!paramKV) {
        return
      }
      // environment awareness
      this.checkAndForceMergeParameter(BUILT_IN_PARAM_MAP.STACK, cluster.stack, paramKV)
      this.checkAndForceMergeParameter(BUILT_IN_PARAM_MAP.ENVIRONMENT, cluster.name, paramKV)
      varDefinitions.push({
        name: BUILT_IN_PARAM_MAP.STACK,
        builtIn: true,
        type: 'string'
      })
      varDefinitions.push({
        name: BUILT_IN_PARAM_MAP.ENVIRONMENT,
        builtIn: true,
        type: 'string'
      })

      if (!this.preInjectedEnvVars.has(cluster.name)) {
        this.preInjectedEnvVars.set(cluster.name, new Map())
      }
      const preInjectedVars = this.preInjectedEnvVars.get(cluster.name)
      preInjectedVars?.set(BUILT_IN_PARAM_MAP.STACK.toUpperCase(), cluster.stack)
      preInjectedVars?.set(BUILT_IN_PARAM_MAP.ENVIRONMENT.toUpperCase(), cluster.name)

      if (cluster.envLocators.length > 0) {
        const levelValues = cluster.envLocators.slice(1)
        // env levels are customized setting that defines each level of env hierarchy (except stack), defaults to ['region', 'zone']
        // the naming of each level could be used as parameter within the templates, you could define others like ['globalCluster', 'dataCenter', 'cell'] etc.
        if (envLevelDefinitions) {
          envLevelDefinitions.forEach((levelName, idx) => {
            paramKV[levelName] = levelValues[idx] || ''
            preInjectedVars?.set(levelName.toUpperCase(), levelValues[idx] || '')
            varDefinitions.push({
              name: levelName,
              builtIn: true,
              type: 'string'
            })
          })
        } else {
          paramKV[BUILT_IN_PARAM_MAP.REGION] = levelValues[0] || ''
          paramKV[BUILT_IN_PARAM_MAP.ZONE] = levelValues[1] || ''
          varDefinitions.push({
            name: BUILT_IN_PARAM_MAP.REGION,
            builtIn: true,
            type: 'string'
          })
          varDefinitions.push({
            name: BUILT_IN_PARAM_MAP.ZONE,
            builtIn: true,
            type: 'string'
          })
          preInjectedVars?.set(BUILT_IN_PARAM_MAP.REGION.toUpperCase(), levelValues[0] || '')
          preInjectedVars?.set(BUILT_IN_PARAM_MAP.ZONE.toUpperCase(), levelValues[0] || '')
        }
      }

      // add metadata to param list when not be set (to allow metadata be referred from each other)
      for (const metaKey in model.metadata) {
        if (!paramKV[metaKey]) {
          paramKV[metaKey] = model.metadata[metaKey]
        }
      }
      // resolve placeholders in metadata and set to parameters
      for (const metaKey in model.metadata) {
        const metaVal = model.metadata[metaKey]
        if (!metaVal) {
          continue
        }
        if (typeof metaVal === 'object') {
          const finalVal = yaml.load(this.resolvePlaceHolders(yaml.dump(metaVal, { noRefs: true }), true, paramKV))
          paramKV[metaKey] = finalVal
        } else if (typeof metaVal === 'string') {
          const finalVal = this.resolvePlaceHolders(metaVal, false, paramKV)
          paramKV[metaKey] = finalVal
        }
        // write back meta key with rendered value
        const val = paramKV[metaKey]
        model.metadata[metaKey] = val
        varDefinitions.push({
          name: metaKey,
          builtIn: true,
          type: val instanceof Array ? 'array' : (typeof val)
        })
      }
      const multiEnvMode = isMultiEnvClusterMode(cluster.name, clientConf?.multiEnvModeClusters)
      paramKV[BUILT_IN_PARAM_MAP.WORKLOAD] = multiEnvMode ? generateWorkloadNameWithEnv(model.metadata.app, cluster.name) : model.metadata.app
      varDefinitions.push({
        name: BUILT_IN_PARAM_MAP.WORKLOAD,
        builtIn: true,
        type: 'string'
      })

      // Loop TWICE to resolve all reference from values to values
      this.walkObject(paramKV, paramKV)
      this.walkObject(paramKV, paramKV)
    })
    this.log.info(`dynamic parameters loaded from 'values' directory`)
    varDefinitions.sort((a, b) => {
      if (a.builtIn && b.builtIn) {
        // move global vars to back
        const isGlobalA = a.name.startsWith('global')
        const isGlobalB = b.name.startsWith('global')
        if (isGlobalA && !isGlobalB) {
          return -1
        }
        // no sort
        return 1
      } else if (a.builtIn || b.builtIn) {
        return a.builtIn ? 1 : -1
      } else {
        return a.name > b.name ? 1 : -1
      }
    })
    return { values: result, varDefinitions, rawParameters }
  }

  public async renderTemplate(workDir: string, relativePath: string, handleInclude: boolean, customizedValues: KV, recordMissingKeys?: string[]): Promise<string> {
    this.log.info(`read template file: ${relativePath}`)
    const isYaml = relativePath.endsWith('.yml') || relativePath.endsWith('.yaml')
    let str = await this.checkAndReadFile(workDir, relativePath)

    // handle include('relative/path/to/file.yaml')
    if (isYaml && handleInclude) {
      // if the template string has include expression, recursively resolve it, eg: include("path/to/another/file.yaml")
      const obj = yaml.load(str)
      await this.resolveIncludeExpression(workDir, obj)
      str = yaml.dump(obj, { noRefs: true })
    }
    // resolve placeholder like '~{ some.param, default-value }', with real value of current stack-env
    return this.resolvePlaceHolders(str, isYaml, customizedValues, recordMissingKeys)
  }

  public async loadYaml(workDir: string, filepath: string): Promise<unknown> {
    const str = await this.checkAndReadFile(workDir, filepath)
    return yaml.load(str)
  }

  public resolvePlaceHolders(tpl: string, isYaml: boolean, customizedValues: KV, recordMissingKeys?: string[]): string {
    if (!isYaml) {
      return this.renderExpr(tpl, customizedValues, recordMissingKeys)
    }
    try {
      const obj = yaml.load(tpl)
      this.walkObject(obj as KV, customizedValues, recordMissingKeys)
      return yaml.dump(obj, { noRefs: true })
    } catch (e) {
      // in case of Yaml array, 'load' will result in error
      const objArr = yaml.loadAll(tpl)
      for (const obj of objArr) {
        this.walkObject(obj as KV, customizedValues, recordMissingKeys)
      }
      return yaml.dump(objArr, { noRefs: true })
    }
  }

  public walkObject(obj: KV, params: KV, recordMissingKeys?: string[]): void {
    if (typeof obj === "object") {
      const isArray = obj instanceof Array
      for (const prop in obj) {
        const value = obj[prop]
        // check and replace property values of the object
        if (typeof value === "string") {
          obj[prop] = this.renderExpr(value, params, recordMissingKeys)
        } else if (typeof value === "object") {
          this.walkObject(value as KV, params, recordMissingKeys)
        }
        // finish values check, now render property name itself
        const newProp = this.renderExpr(prop, params, recordMissingKeys)
        if (newProp != prop) {
          obj[newProp] = obj[prop]
          delete obj[prop]
        }
      }
      if (isArray) {
        let i = obj.length
        while (i--) {
          if (obj[i] === undefined || obj[i] === null) {
            obj.splice(i, 1)
          }
        }
      }
    } else {
      this.log.error(`render values on wrong type: ${obj}`)
    }
  }

  private checkAndForceMergeParameter(key: string, value: string | unknown, currentMap: KV) {
    if (value === undefined) {
      return
    }
    const val = typeof value !== "string" ? JSON.stringify(value) : value
    if (currentMap[key] !== undefined) {
      this.log.warn(`pre-defined parameter '${key}' can not be set by user, current value is ${val}, user-defined value will be IGNORED: ${currentMap[key]}`)
    }
    this.log.info(`find meta parameter: ${key} => ${val}`)
    currentMap[key] = value
  }

  public async readCustomizedValuesRecursive(
    rawParameters: Map<string, KV>, workingDir: string,
    relativePath: string, depth: number, checkAbsolutePath?: boolean
  ): Promise<VariableDefinition[]> {
    const varDefArray: VariableDefinition[] = []
    let valDirOrFile = path.join(workingDir, relativePath)
    this.log.info(`load dynamic parameters from ${valDirOrFile}`)
    if (fs.pathExistsSync(valDirOrFile)) {
      this.log.info(`resolving parameters in '${valDirOrFile}'`)
    } else if (checkAbsolutePath && fs.pathExistsSync(relativePath)) {
      valDirOrFile = relativePath
      workingDir = path.dirname(relativePath)
      relativePath = path.basename(relativePath)
      this.log.info(`absolute path mode detected, start resolving parameters in file/dir: ${relativePath}`)
    } else {
      this.log.info(`no values directory/files found in '${valDirOrFile}', skipped`)
      return []
    }
    if (depth >= 6) {
      return []
    }
    if (fs.statSync(valDirOrFile).isDirectory()) {
      const valueFiles = await fs.readdir(valDirOrFile)
      for (const file of valueFiles) {
        const relativeDir = path.join(relativePath, file)
        const ret = await this.readCustomizedValuesRecursive(rawParameters, workingDir, relativeDir, depth + 1)
        varDefArray.push(...ret)
      }
    } else {
      if (!this.isYaml(valDirOrFile)) {
        return []
      }
      const obj = await this.loadYaml(workingDir, relativePath) as KV
      const parameters = Object.keys(obj)
      for (const param of parameters) {
        if (!rawParameters.has(param)) {
          rawParameters.set(param, {})
        }
        const envKV = rawParameters.get(param)
        if (!envKV) {
          continue
        }
        const mergeSource = obj[param] as KV
        let varType = 'string'
        let varTypeLocked = false
        for (const envName in mergeSource) {
          const val = mergeSource[envName]
          envKV[envName] = val
          if (!varTypeLocked && val !== undefined && val !== null) {
            varTypeLocked = true
            varType = val instanceof Array ? 'array' : (typeof val)
          }
        }
        varDefArray.push({
          name: param,
          type: varType,
          filename: relativePath,
          builtIn: false
        })
      }
    }
    return varDefArray
  }

  public async checkAndReadFile(workDir: string, relativePath: string): Promise<string> {
    if (this.fileContentCache.has(relativePath)) {
      return this.fileContentCache.get(relativePath) as string
    }
    const fPath = path.join(workDir, relativePath)
    if (!fs.existsSync(fPath)) {
      throw new Error(`file: ${fPath} not exists`)
    }
    const buffer = await fs.readFile(fPath)
    this.fileContentCache.set(relativePath, buffer.toString())
    return this.fileContentCache.get(relativePath) as string
  }

  private async resolveIncludeExpression(workingDir: string, obj: unknown) {
    if (obj instanceof Array) {
      for (const index in obj) {
        const entry = obj[index]
        if (typeof entry === "string" && includeTemplateMatcher.test(entry)) {
          obj[index] = await this.checkAndLoadIncludedFile(workingDir, entry)
        } else if (typeof entry === "object") {
          await this.resolveIncludeExpression(workingDir, entry)
        }
      }
    }
    for (const key in obj as KV) {
      const value = (obj as KV)[key]
      if (typeof value === "string" && includeTemplateMatcher.test(value)) {
        (obj as KV)[key] = await this.checkAndLoadIncludedFile(workingDir, value)
      } else if (typeof value === "object") {
        this.resolveIncludeExpression(workingDir, value)
      }
    }
  }

  private isYaml(file: string) {
    return file.endsWith('.yaml') || file.endsWith('.yml')
  }

  private async checkAndLoadIncludedFile(workingDir: string, expr: string) {
    includeTemplateMatcher.lastIndex = 0
    const path = (includeTemplateMatcher.exec(expr) as RegExpExecArray)[1]
    if (this.includeCache.has(path)) {
      this.log.error(`${expr} could not be applied because ${path} has been included by other file`)
      throw new Error(`can not include ${path} again`)
    }
    // will throw error if included file not exists
    const result = await this.loadYaml(workingDir, path)
    this.includeCache.add(path)
    return result
  }

  private parseBaseType(str?: string): string | boolean | number | undefined {
    return parseBaseType(str)
  }

  /**
   * replace ~{ param, default_val } placeholder into real value, depends on values/ parameters
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private renderExpr(str: string, values: KV, recordMissingKeys?: string[]): any {
    let strExpr = str.trim()
    let renderedStr = ''
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const exprStartIdx = strExpr.indexOf('~{')
      if (exprStartIdx === -1) {
        return renderedStr === '' ? strExpr : renderedStr + strExpr
      }

      renderedStr += strExpr.substring(0, exprStartIdx)
      strExpr = strExpr.substring(exprStartIdx)

      const [value, tailContent] = execExpr(strExpr, values, key => recordMissingKeys?.push(key), renderHelpers)
      if (tailContent === '' && renderedStr === '') {
        // exact match
        return value
      }
      renderedStr += value
      if (tailContent === '') {
        break
      }
      strExpr = tailContent
    }
    return renderedStr
  }
}

export function composeValuesByEnv(envLocators: string[], allEnvValues: { [k: string]: KV } | Map<string, KV>, prefix = ''): KV {
  function getValue(envLocators: string[], envValues: KV): unknown {
    if (envLocators.length === 0) {
      return envValues[DEFAULT_ENV_KEY] ?? undefined
    }
    const envKey = envLocators.join('/')
    envLocators.pop()
    if (envKey in envValues) {
      return envValues[envKey]
    }
    return getValue(envLocators, envValues)
  }

  if (allEnvValues instanceof Map) {
    return [...allEnvValues.keys()].reduce((prev, k) => {
      console.log(k, allEnvValues.get(k))
      prev[prefix + k] = getValue(
        [...envLocators],
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        allEnvValues.get(k)!
      )
      return prev
    }, {} as KV)
  } else {
    return Object.keys(allEnvValues).reduce((prev, k) => {
      prev[prefix + k] = getValue([...envLocators], allEnvValues[k])
      return prev
    }, {} as KV)
  }
}

export const renderHelpers: { [key: string]: (...args: unknown[]) => unknown } = {
  replace: (value: string, search: string, replaceValue: string): string => {
    if (typeof value !== "string" || typeof search !== "string" || typeof replaceValue !== "string") {
      // wrong parameter
      return value
    }
    return value.replace(new RegExp(search, "gi"), replaceValue)
  },

  toLower: (value: string): string => {
    if (typeof value !== "string") {
      return value
    }
    return value.toLowerCase()
  },

  toUpper: (value: string): string => {
    if (typeof value !== "string") {
      return value
    }
    return value.toUpperCase()
  },

  toJson: (obj: KV): string => {
    return JSON.stringify(obj)
  },

  substring: (value: string, start: number, end?: number): string => {
    if (typeof start !== "number") {
      // wrong parameter
      return value
    }
    if (!end) {
      return value.substring(start)
    }
    if (end <= 0 && end < start) {
      end = value.length - end
    }
    return value.substring(start, end)
  },

  indent: (value: string, indentCount: number): string => {
    if (typeof value !== "string" || typeof indentCount !== "number") {
      return value
    }
    if (!indentCount || indentCount > 64) {
      return value
    } else {
      const spaces = " ".repeat(indentCount)
      return value.split('\n').map(x => spaces + x).join('\n')
    }
  },

  trim: (value: string): string => {
    if (typeof value !== "string") {
      return value
    }
    return value.trim()
  },

  quote: (value: string): string => {
    if (typeof value !== "string") {
      return value
    }
    return '"' + value + '"'
  },

  toString(value: { toString: () => string } | null | boolean): string {
    if (value === null || typeof value === 'boolean') {
      return `${value}`
    }
    return value.toString()
  },

  randomStr: (_: string, length?: number): string => {
    return randomAlphabetNumeric(length || 32)
  },

  dateTime: (): string => {
    const time = new Date()
    return time.toISOString().replace(/:|\./g, '-')
  },

  date: (): string => {
    const time = new Date()
    return time.toISOString().split('T')[0]
  },

  now: () => {
    return Date.now().toString()
  },

  not: (value: unknown): boolean => {
    return !value
  }
}

export type VariableDefinition = {
  name: string
  filename?: string
  type: 'number' | 'string' | 'boolean' | 'array' | 'object' | string
  builtIn: boolean
  merged?: unknown
  current?: unknown
}
